/**
 * Endpoints de **Logística → Notas de Crédito**
 * (`/api/logistica/notas-credito`).
 *
 * Sólo consulta: el listado va paginado en el servidor con los filtros del panel
 * anterior (fechas, serie, número y estado) más el buscador libre, y cada fila trae
 * los enlaces de descarga de PDF, XML y CDR.
 */

import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type { NotasCreditoFiltros, NotasCreditoRespuesta } from './notas-credito.types';

export const notasCreditoKeys = {
  lista: (filtros: NotasCreditoFiltros) => ['logistica', 'notas-credito', filtros] as const,
};

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: NotasCreditoFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.serie) parametros.set('serie_nota', filtros.serie);
  if (filtros.numero) parametros.set('numero_nota', filtros.numero);
  if (filtros.estado) parametros.set('estado_api', filtros.estado);
  if (filtros.search) parametros.set('search', filtros.search);
  if (filtros.page) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const notasCreditoApi = {
  listar: (
    filtros: NotasCreditoFiltros = {},
    signal?: AbortSignal,
  ): Promise<NotasCreditoRespuesta> =>
    apiClient.get<NotasCreditoRespuesta>(`/api/logistica/notas-credito${cadenaDe(filtros)}`, {
      signal,
    }),
};

/** Notas de crédito del filtro pedido. */
export function useNotasCredito(filtros: NotasCreditoFiltros = {}) {
  return useQuery({
    queryKey: notasCreditoKeys.lista(filtros),
    queryFn: ({ signal }) => notasCreditoApi.listar(filtros, signal),
  });
}
